// =============================================================================
// Optim TURPE V2 — LE DOSSIER DE L'ÉTUDE, RÉSERVÉ À L'ÉQUIPE (DEV-1913, DEV-2311).
//
// Ce que l'équipe doit vivre : depuis une session de l'équipe, elle copie un
// dossier déjà rempli pour retrouver le cas, au lieu d'un aller-retour pour
// obtenir l'identifiant de l'étude et la période. Depuis le 05/10/2026, ce
// dossier n'est plus jamais montré au client (`OptimTurpeV2DossierEquipe`) :
// il expose des détails techniques (identifiants, code de cause, versions).
//
// Ce que le dossier contient, et rien d'autre : l'identifiant de l'étude, le
// compteur, la période, la provenance des entrées qui pèsent, la version du
// moteur, et la cause servie s'il y en a une. **Aucune donnée personnelle** : ni
// nom, ni adresse, ni courriel, ni identifiant d'auteur de surcharge — un
// identifiant d'utilisateur n'ajoute rien au diagnostic et voyagerait dans un
// courriel.
//
// La page ne poste rien et n'écrit à personne : le dossier se copie.
// =============================================================================

import { libelleChamp } from "@modules/courtage/utils/optim-turpe-v2.libelles"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { lireEchec } from "@modules/courtage/utils/optim-turpe-v2.echec"
import {
    entreesQuiPesent,
    lireRegistre,
} from "@modules/courtage/utils/optim-turpe-v2.registre"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

/** Une ligne « Clé : valeur », ou rien quand la valeur n'est pas connue. */
function ligne(cle: string, valeur: unknown): string | null {
    if (valeur === null || valeur === undefined || valeur === "") return null
    return `${cle} : ${String(valeur)}`
}

/**
 * Le dossier support pré-rempli, en texte brut copiable.
 *
 * Volontairement du texte et non un JSON : il est collé dans un courriel et lu
 * par une personne. Les champs absents ne laissent pas de ligne vide — un
 * dossier qui affirme « Période : — » ne dit rien de plus qu'une ligne absente,
 * et invite à croire que la valeur a été cherchée.
 */
export function construireDossierSupport(etude: OptimTurpeV2Etude): string {
    const chiffre = lireChiffre(etude)
    const echec = lireEchec(etude)
    const registre = lireRegistre(etude)
    const periode =
        chiffre.periode.dateFrom && chiffre.periode.dateTo
            ? `${chiffre.periode.dateFrom} → ${chiffre.periode.dateTo}` +
              (chiffre.periode.jours !== null
                  ? ` (${chiffre.periode.jours} jours)`
                  : "")
            : null

    const lignes: Array<string | null> = [
        "Dossier d'étude Optimisation TURPE",
        "",
        ligne("Identifiant de l'étude", etude._id),
        ligne("Compteur (PDL)", etude.source?.idsource),
        ligne("Segment", etude.compteur?.segment),
        ligne("Période analysée", periode),
        ligne("État servi", chiffre.etat),
        ligne("Motif", chiffre.etatMotif),
        ligne("Cause du montant non servi", chiffre.montantNonServiCause),
        ligne("Cause du refus", echec?.code),
        ligne("Date de calcul", etude.calcul?.calcule_le ?? etude.created_at),
        ligne("Version du moteur", etude.calcul?.versions?.moteur),
        ligne("Version du pricer", etude.calcul?.versions?.pricer),
        ligne("Empreinte courtage", etude.calcul?.versions?.courtage),
        ligne("Date de barème retenue", etude.calcul?.bareme?.date_retenue),
    ]

    const pesent = entreesQuiPesent(registre)
    if (pesent.length > 0) {
        lignes.push("", "Provenance des entrées qui pèsent sur le résultat :")
        for (const entree of pesent) {
            const provenance = entree.provenanceLibelle ?? entree.provenance
            const unite = entree.unite ? ` ${entree.unite}` : ""
            lignes.push(
                `- ${libelleChamp(entree.champ)} : ${String(
                    entree.valeur ?? "non renseignée"
                )}${unite} (${provenance ?? "provenance non servie"}` +
                    `${entree.estSurchargee ? ", corrigée par le client" : ""})`
            )
        }
    } else if (registre === null) {
        lignes.push(
            "",
            "Provenance des entrées : non servie par cette version du moteur."
        )
    }

    return lignes.filter((l): l is string => l !== null).join("\n")
}
